import { FC, useState, useEffect, useCallback } from 'react'
import { X, Copy, Check, Pencil, Eye, Save, Loader2, FileText, Clock, Sparkles } from 'lucide-react'
import { Button } from '../ui/button'
import { useChat } from '../../contexts/ChatContext'
import { cn } from '../../lib/utils'
import api from '../../services/api'
import { TipTapEditor } from '../ui/TipTapEditor'
import { DocumentBody } from '../document/DocumentBody'
import { getDocTypeStyles } from '../document/docTypeStyles'

/**
 * Clinical Manuscript Panel
 * A refined artifact viewing and editing experience with elegant typography
 * and smooth transitions between view/edit modes
 */
export const ArtifactPanel: FC = () => {
  const { expandedArtifact, expandArtifact, updateMessage } = useChat()
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Sync edited content when artifact changes
  useEffect(() => {
    if (expandedArtifact?.artifact?.content) {
      setEditedContent(expandedArtifact.artifact.content)
      setHasChanges(false)
    }
  }, [expandedArtifact?.artifact?.content])

  // Track changes
  useEffect(() => {
    if (expandedArtifact?.artifact?.content) {
      setHasChanges(editedContent !== expandedArtifact.artifact.content)
    }
  }, [editedContent, expandedArtifact?.artifact?.content])

  const handleCopy = async () => {
    try {
      const contentToCopy = isEditing ? editedContent : expandedArtifact?.artifact?.content || ''
      await navigator.clipboard.writeText(contentToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const handleClose = useCallback(() => {
    if (hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirm) return
    }
    setIsEditing(false)
    setHasChanges(false)
    expandArtifact(null)
  }, [hasChanges, expandArtifact])

  const handleSave = async () => {
    if (!expandedArtifact?.artifact?.artifact_id || !hasChanges) return

    setIsSaving(true)
    try {
      await api.post(`/api/v1/artifacts/${expandedArtifact.artifact.artifact_id}/versions`, {
        content: editedContent,
        is_manual_save: true,
        edit_description: "User edit"
      })

      if (expandedArtifact.id) {
        updateMessage(expandedArtifact.id, {
          artifact: {
            ...expandedArtifact.artifact,
            content: editedContent
          }
        })
      }

      expandArtifact({
        ...expandedArtifact,
        artifact: {
          ...expandedArtifact.artifact,
          content: editedContent
        }
      })

      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save artifact:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const toggleEditMode = () => {
    if (isEditing && hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Discard them?')
      if (!confirm) return
      setEditedContent(expandedArtifact?.artifact?.content || '')
      setHasChanges(false)
    }
    setIsEditing(!isEditing)
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (!isEditing || !hasChanges)) {
        handleClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && isEditing && hasChanges) {
        e.preventDefault()
        handleSave()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault()
        toggleEditMode()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isEditing, hasChanges, handleClose])

  if (!expandedArtifact?.artifact) return null

  const { artifact, metadata } = expandedArtifact

  const docStyle = getDocTypeStyles(artifact.type)

  return (
    <div className={cn(
      "flex h-full w-[500px] flex-shrink-0 flex-col",
      "bg-background border-l border-border/50",
      "animate-slide-in-right"
    )}>
      {/* Header - Elegant glass morphism */}
      <div className={cn(
        "flex items-center justify-between px-5 py-4",
        "bg-surface-elevated/80 backdrop-blur-md",
        "border-b border-border/40"
      )}>
        <div className="flex items-center gap-3 min-w-0">
          {/* Document icon */}
          <div className={cn(
            "flex items-center justify-center w-10 h-10 rounded-lg",
            "bg-gradient-to-br from-clinical-teal/20 to-clinical-cyan/10",
            "border border-clinical-teal/20"
          )}>
            <FileText className="w-5 h-5 text-clinical-teal" />
          </div>

          <div className="min-w-0">
            <h3 className="font-display font-semibold text-foreground truncate">
              {artifact.title}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium uppercase tracking-wide",
                docStyle.bg, docStyle.text, "border", docStyle.border
              )}>
                {artifact.type}
              </span>
              {hasChanges && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-clinical-amber/15 text-clinical-amber border border-clinical-amber/30">
                  <Sparkles className="w-3 h-3" />
                  Unsaved
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {/* Edit/View toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleEditMode}
            className={cn(
              "h-9 w-9 p-0 rounded-lg",
              "hover:bg-clinical-teal/10 hover:text-clinical-teal",
              "transition-all duration-150",
              isEditing && "bg-clinical-teal/15 text-clinical-teal"
            )}
            title={isEditing ? "View mode (Ctrl+E)" : "Edit mode (Ctrl+E)"}
          >
            {isEditing ? (
              <Eye className="h-4 w-4" />
            ) : (
              <Pencil className="h-4 w-4" />
            )}
          </Button>

          {/* Save button */}
          {isEditing && hasChanges && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className={cn(
                "h-9 w-9 p-0 rounded-lg",
                "bg-clinical-sage/15 text-clinical-sage",
                "hover:bg-clinical-sage/25",
                "transition-all duration-150"
              )}
              title="Save changes (Ctrl+S)"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* Copy button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className={cn(
              "h-9 w-9 p-0 rounded-lg",
              "hover:bg-muted",
              "transition-all duration-150",
              copied && "bg-clinical-sage/15 text-clinical-sage"
            )}
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>

          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-9 w-9 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all duration-150"
            title="Close panel (Esc)"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content - TipTap Editor or Rendered View */}
      <div className="flex-1 overflow-hidden">
        {isEditing ? (
          /* Edit Mode - TipTap WYSIWYG Editor */
          <TipTapEditor
            content={editedContent}
            onChange={setEditedContent}
            placeholder="Begin documenting your clinical notes..."
            editable={true}
            className="h-full border-0 rounded-none"
          />
        ) : (
          /* View Mode - Rendered markdown with manuscript styling */
          <div className="h-full overflow-y-auto">
            <div className={cn(
              "p-6",
              "bg-gradient-to-b from-surface-elevated to-surface",
              // Subtle paper texture effect
              "relative before:absolute before:inset-0 before:pointer-events-none",
              "before:bg-[radial-gradient(circle_at_50%_0%,_hsl(var(--clinical-teal)/0.02)_0%,_transparent_50%)]"
            )}>
              <DocumentBody content={artifact.content} />
            </div>
          </div>
        )}
      </div>

      {/* Footer - Metadata and keyboard hints */}
      <div className={cn(
        "px-5 py-3",
        "bg-muted/30 backdrop-blur-sm",
        "border-t border-border/40"
      )}>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {/* Metadata */}
          <div className="flex items-center gap-4">
            {metadata?.tokens && (
              <span>{metadata.tokens} tokens</span>
            )}
            {metadata?.latency && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {metadata.latency}ms
              </span>
            )}
          </div>

          {/* Keyboard shortcuts */}
          <div className="flex items-center gap-2 opacity-60">
            {isEditing ? (
              <>
                <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Ctrl+S</kbd>
                <span>save</span>
                <span className="text-border">•</span>
                <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Ctrl+E</kbd>
                <span>view</span>
              </>
            ) : (
              <>
                <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Ctrl+E</kbd>
                <span>edit</span>
                <span className="text-border">•</span>
                <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Esc</kbd>
                <span>close</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
